// Delivery schedules: turn a cadence into dated releases. Pure and dependency-free, so the browser shows a live
// preview with the same code the API uses to save. Dates are ISO strings (YYYY-MM-DD) handled in UTC, so a
// date never shifts with the viewer's time zone.

export type Repeat = "weekly" | "days" | "monthly";
export type ScheduleShipTo = { name?: string; addressLine1?: string; city?: string; state?: string; postCode?: string; country?: string; share: number } ;
export type Schedule = {
  start: string; // first possible ship date
  repeat: Repeat;
  every: number; // every N weeks / days / months
  weekdays?: number[]; // weekly: 0 = Sunday … 6 = Saturday
  monthDay?: number | "last"; // monthly: day of the month, or the last day
  end: { kind: "count"; count: number } | { kind: "until"; date: string };
  // each: the same quantity every delivery, for as many deliveries as the end allows; total: a total split across
  // the deliveries; rate: `lines` per delivery until `totals` has shipped, so the number of deliveries follows.
  quantity: { mode: "each" | "total" | "rate"; lines: Record<string, number>; totals?: Record<string, number> };
  shipTos: ScheduleShipTo[]; // each delivery is split across these by share (%); empty = the customer's address
  weekendsToFriday?: boolean; // every-N-days and monthly dates that land on a weekend ship the Friday before
};
export type GeneratedRelease = { shipDate: string; shipTo: Omit<ScheduleShipTo, "share"> | null; quantities: Record<string, number> };

export const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MAX_DELIVERIES = 520;
const DAY = 864e5;
const isDate = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !isNaN(Date.parse(v));
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const at = (d: string) => Date.parse(d + "T00:00:00Z");
const round4 = (x: number) => Math.round(x * 10000) / 10000;

/** The delivery dates a cadence produces, in order. */
export function deliveryDates(s: Schedule): string[] {
  const out: string[] = [];
  const startT = at(s.start);
  const limitT = s.end.kind === "until" ? at(s.end.date) : Infinity;
  const count = s.end.kind === "count" ? Math.min(s.end.count, MAX_DELIVERIES) : MAX_DELIVERIES;
  const every = Math.max(1, Math.floor(s.every || 1));
  const push = (t: number) => {
    let d = t;
    const wd = new Date(d).getUTCDay();
    if (s.weekendsToFriday && s.repeat !== "weekly" && (wd === 0 || wd === 6)) d -= (wd === 6 ? 1 : 2) * DAY;
    const v = iso(d);
    if (d >= startT && d <= limitT && out.at(-1) !== v) out.push(v);
  };
  if (s.repeat === "days") {
    for (let t = startT; out.length < count && t <= limitT; t += every * DAY) push(t);
  } else if (s.repeat === "weekly") {
    const days = [...new Set((s.weekdays ?? []).filter((d) => d >= 0 && d <= 6))].sort();
    if (!days.length) return [];
    const sunday = startT - new Date(startT).getUTCDay() * DAY; // the week the schedule starts in
    for (let w = 0; out.length < count; w += every) {
      const base = sunday + w * 7 * DAY;
      if (base > limitT) break;
      for (const d of days) { if (out.length >= count) break; const t = base + d * DAY; if (t >= startT) push(t); }
    }
  } else {
    const s0 = new Date(startT);
    for (let m = 0; out.length < count; m += every) {
      const y = s0.getUTCFullYear(), mo = s0.getUTCMonth() + m;
      const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
      const day = s.monthDay === "last" ? last : Math.min(Math.max(1, Math.floor(Number(s.monthDay) || s0.getUTCDate())), last);
      const t = Date.UTC(y, mo, day);
      if (t > limitT + 3 * DAY) break;
      push(t);
      if (m > MAX_DELIVERIES * 12) break;
    }
  }
  return out.slice(0, count);
}

/** Split `total` into `n` parts: whole units when the total is whole (the first deliveries take the remainder). */
function split(total: number, n: number): number[] {
  if (n <= 0) return [];
  if (Number.isInteger(total)) {
    const base = Math.floor(total / n), rest = total - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < rest ? 1 : 0));
  }
  const each = round4(total / n);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? round4(total - each * (n - 1)) : each));
}

/** Check a schedule; returns a message a person can act on, or null. */
/** Rate mode: how many deliveries it takes to ship every line's total at its quantity per delivery. */
export function rateDeliveries(s: Schedule, lineIds: string[]): number {
  let n = 0;
  for (const id of lineIds) {
    const per = Number(s.quantity.lines[id]) || 0, total = Number(s.quantity.totals?.[id]) || 0;
    if (per > 0 && total > 0) n = Math.max(n, Math.ceil(round4(total / per)));
  }
  return n;
}
/** The schedule as it runs: in rate mode the end is the number of deliveries the totals need. */
export const effective = (s: Schedule, lineIds: string[]): Schedule => (s.quantity?.mode === "rate" ? { ...s, end: { kind: "count", count: Math.max(1, rateDeliveries(s, lineIds)) } } : s);

export function scheduleProblem(s: Schedule | null | undefined, lineIds: string[]): string | null {
  if (!s || typeof s !== "object") return "Set up the schedule first.";
  if (!isDate(s.start)) return "Pick the first ship date.";
  if (!["weekly", "days", "monthly"].includes(s.repeat)) return "Choose how often deliveries repeat.";
  if (!(Number(s.every) >= 1) || Number(s.every) > 365) return "Repeat every must be between 1 and 365.";
  if (s.repeat === "weekly" && !(s.weekdays ?? []).length) return "Pick at least one day of the week.";
  if (s.quantity?.mode === "rate") {
    const per = s.quantity.lines ?? {}, tot = s.quantity.totals ?? {};
    if (!lineIds.some((id) => Number(tot[id]) > 0)) return "Enter a total for at least one line.";
    const missing = lineIds.find((id) => Number(tot[id]) > 0 && !(Number(per[id]) > 0));
    if (missing) return "Every line with a total needs a quantity per delivery.";
    if ([...Object.values(per), ...Object.values(tot)].some((v) => !Number.isFinite(Number(v)) || Number(v) < 0)) return "Quantities must be zero or more.";
    if (rateDeliveries(s, lineIds) > MAX_DELIVERIES) return `That takes more than ${MAX_DELIVERIES} deliveries. Raise the quantity per delivery.`;
    s = effective(s, lineIds);
  }
  if (s.end?.kind === "count" && !(Number(s.end.count) >= 1 && Number(s.end.count) <= MAX_DELIVERIES)) return `Number of deliveries must be between 1 and ${MAX_DELIVERIES}.`;
  if (s.end?.kind === "until" && (!isDate(s.end.date) || s.end.date < s.start)) return "The end date must be on or after the first ship date.";
  if (!s.end || !["count", "until"].includes(s.end.kind)) return "Choose when the schedule ends.";
  const q = s.quantity?.lines ?? {};
  if (!lineIds.some((id) => Number(q[id]) > 0)) return "Enter a quantity for at least one line.";
  if (lineIds.some((id) => q[id] !== undefined && (!Number.isFinite(Number(q[id])) || Number(q[id]) < 0))) return "Quantities must be zero or more.";
  const shares = s.shipTos ?? [];
  if (shares.length > 20) return "Up to 20 ship-to addresses.";
  if (shares.some((x) => !(Number(x.share) > 0))) return "Every ship-to needs a share above 0%.";
  if (shares.length && Math.abs(shares.reduce((t, x) => t + Number(x.share), 0) - 100) > 0.01) return "Ship-to shares must add up to 100%.";
  return null;
}

/** The releases a schedule produces: one per delivery date and ship-to, with quantities per contract line. */
export function generateReleases(s: Schedule, lineIds: string[]) {
  s = effective(s, lineIds);
  const dates = deliveryDates(s);
  const shipTos = s.shipTos?.length ? s.shipTos : [{ share: 100 }];
  const perLine: Record<string, number[]> = {};
  for (const id of lineIds) {
    const q = Number(s.quantity.lines[id]) || 0;
    if (s.quantity.mode === "rate") {
      // The quantity per delivery until the total has shipped; the last delivery takes what's left.
      let left = Number(s.quantity.totals?.[id]) || 0;
      perLine[id] = dates.map(() => { const v = round4(Math.min(q, Math.max(0, left))); left = round4(left - v); return v; });
    } else perLine[id] = s.quantity.mode === "total" ? split(q, dates.length) : dates.map(() => q);
  }
  const releases: GeneratedRelease[] = [];
  dates.forEach((d, i) => {
    // Split this delivery's quantity across ship-tos by share; the last ship-to takes what rounding leaves.
    const parts = shipTos.map(() => ({} as Record<string, number>));
    for (const id of lineIds) {
      const q = perLine[id][i];
      let left = q;
      shipTos.forEach((st, j) => {
        const v = j === shipTos.length - 1 ? round4(left) : Number.isInteger(q) ? Math.round((q * Number(st.share)) / 100) : round4((q * Number(st.share)) / 100);
        parts[j][id] = v; left -= v;
      });
    }
    shipTos.forEach((st, j) => {
      const quantities = Object.fromEntries(Object.entries(parts[j]).filter(([, v]) => v > 0));
      if (!Object.keys(quantities).length) return;
      const { share, ...addr } = st as ScheduleShipTo;
      void share;
      releases.push({ shipDate: d, shipTo: Object.values(addr).some(Boolean) ? addr : null, quantities });
    });
  });
  const totals = Object.fromEntries(lineIds.map((id) => [id, round4(releases.reduce((t, r) => t + (r.quantities[id] ?? 0), 0))]));
  return { dates, releases, totals };
}

/** "Weekly on Tuesday and Friday", "Every 10 days", "Monthly on the last day", for the summary line. */
export function describeCadence(s: Schedule): string {
  const n = Math.max(1, Math.floor(s.every || 1));
  if (s.repeat === "days") return n === 1 ? "Every day" : `Every ${n} days`;
  if (s.repeat === "weekly") {
    const names = (s.weekdays ?? []).slice().sort().map((d) => WEEKDAY_NAMES[d]);
    const list = names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
    return `${n === 1 ? "Weekly" : `Every ${n} weeks`} on ${list}`;
  }
  const day = s.monthDay === "last" ? "the last day" : `day ${s.monthDay ?? new Date(at(s.start)).getUTCDate()}`;
  return `${n === 1 ? "Monthly" : `Every ${n} months`} on ${day}`;
}
